import React, { FC, ReactNode, Suspense } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { initServerTranslation } from '@bts-web/utils-lokalise';
import { DesktopLeftNav } from '../DesktopLeftNav/DesktopLeftNav';
import { DesktopTopHeaderBar } from '../DesktopTopHeaderBar/DesktopTopHeaderBar';
// import { DesktopTopHeaderBarSkeleton } from '../DesktopTopHeaderBar/DesktopTopHeaderBarSkeleton';
import { DesktopLeftNavSkeleton } from '../DesktopLeftNav/DesktopLeftNavSkeleton';
import { LayoutTitleElement, TitleUnionType } from './LayoutTitleElement';
import { TFunction } from 'i18next';
import {
  getMainNavigationTranslations,
  IMainNavigationTranslations,
} from '../utils/getMainNavigationTranslations';
import { FooterLinkListByAppId } from '../DesktopDisclaimerFooter/subcomponents/FooterLinkListByAppId/FooterLinkListByAppId';
import { UKRiskWarningBanner } from '../../UKRiskWarningBanner/UKRiskWarningBanner';
import { getServerFeatureFlag } from '../../../statsig/sdk/server';
import MobileNavWithTradeDynamic from '../MobileNavWithTrade/MobileNavWithTradeDynamic';
import { HiddenMobile } from '../Reusable/HiddenMobile/HiddenMobile';
import { HiddenDesktop } from '../Reusable/HiddenDesktop/HiddenDesktop';
import {
  UK_RISK_WARNING_BANNER_HEIGHT,
  UK_RISK_WARNING_BANNER_HEIGHT_MOBILE,
} from '../../constants';

/**
 * `AppMainLayoutWrapper` is a functional component that provides the main layout for the application.
 *
 * @component
 * @param {object} props - The properties that define the component's behavior and display.
 * @param {(initialKey: string, otherParams?: unknown) => string} props.translationUtil - A utility function for translations.
 * @param {ReactNode} [props.children] - The child components to be rendered within the layout.
 * @param {TitleUnionType} props.title - Object that defines the title of the page, it supports breadcrumbs and string
 * @param {object} [props.settings] - Additional settings for the layout.
 * @param {boolean} [props.settings.hasMobileBottomNav] - Determines if the bottom navigation should be displayed on mobile view.
 * @param {boolean} [props.settings.hideTopBarTradeButton] - Determines if the trade button in the top bar should be hidden, this is for trade pages where the trade process is in progress
 * @param {boolean} [props.settings.hideDesktopFooter] - Determines if the desktop footer should be hidden, used in the pages where the desktop view contains both a trade element and an asset detail element
 * @param {boolean} [props.settings.fullPageBackdropMode] - Makes the layout overlay the entire page, used for modals
 * @example
 * return (
 *   <AppMainLayoutWrapper
 *     translationUtil={translate}
 *     title="Home"
 *     settings={{
 *       hasMobileBottomNav: true,
 *       hideTopBarTradeButton: false,
 *       hideDesktopFooter: false,
 *     }}
 *   >
 *     <Home />
 *   </AppMainLayoutWrapper>
 * );
 *
 * @returns {ReactElement} The rendered `AppMainLayoutWrapper` component.
 */

type hasObjectTitleType = {
  title: TitleUnionType;
  OverWriteTitleElement?: never;
};

type hasComponentTitleType = {
  OverWriteTitleElement: ReactNode;
  title?: never;
};

type TitleType = hasObjectTitleType | hasComponentTitleType;

export const AppMainLayoutWrapper: FC<
  {
    children?: ReactNode;
    settings?: {
      hasMobileBottomNav?: boolean;
      hideTopBarTradeButton?: boolean;
      hideDesktopFooter?: boolean;
      fullPageBackdropMode?: boolean;
      hideUKRiskWarning?: boolean;
    };
    initTranslations?: IMainNavigationTranslations;
  } & TitleType
> = async ({
  children,
  title,
  settings,
  OverWriteTitleElement,
  initTranslations,
}) => {
  let translations = initTranslations;

  if (!initTranslations) {
    const { t: translationUtil } = await initServerTranslation();

    translations = getMainNavigationTranslations(translationUtil as TFunction);
  }

  if (!translations) {
    return;
  }

  const {
    hasMobileBottomNav,
    hideTopBarTradeButton,
    hideDesktopFooter,
    fullPageBackdropMode,
    hideUKRiskWarning,
  } = settings ?? {};

  const feature_isUkLicensed = await getServerFeatureFlag(
    'feature_isUkLicensed',
  );

  return (
    <div
      className={css({
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'neutrals.fill_quinary',
        paddingInlineStart: {
          base: undefined,
          lg: `calc(var(--spacing-extra_small_4) + 73px)`,
        },
        _rtl: {
          paddingInlineStart: { base: undefined, lg: '0' },
          paddingInlineEnd: {
            base: undefined,
            lg: `calc(var(--spacing-extra_small_4) + 73px)`,
          },
        },
        paddingTop: {
          base: undefined,
          lg: `calc(var(--spacing-extra_small_4) + 72px)`,
        },
        height: fullPageBackdropMode ? '100vh' : undefined,
        overflow: fullPageBackdropMode ? 'hidden' : undefined,
      })}
    >
      <HiddenMobile>
        <DesktopTopHeaderBar
          hideTopBarTradeButton={hideTopBarTradeButton ?? false}
          pageTitleElement={
            OverWriteTitleElement ? (
              OverWriteTitleElement
            ) : title ? (
              <LayoutTitleElement title={title} />
            ) : null
          }
          translatedValues={translations}
        />
      </HiddenMobile>

      <HiddenMobile>
        <Suspense fallback={<DesktopLeftNavSkeleton />}>
          <DesktopLeftNav translatedValues={translations} />
        </Suspense>
      </HiddenMobile>

      <main
        style={
          feature_isUkLicensed && !hideUKRiskWarning
            ? ({
                '--layout-sticky-offset': `${UK_RISK_WARNING_BANNER_HEIGHT}px`,
                '--layout-sticky-offset-mobile': `${UK_RISK_WARNING_BANNER_HEIGHT_MOBILE}px`,
              } as React.CSSProperties)
            : undefined
        }
        className={css({
          background: 'neutrals.card_fill_primary',
          display: 'flex',
          alignItems: 'stretch',
          flexDirection: 'column',
          flex: 'auto',
          width: {
            base: '100%',
            lg: '1440px',
          },
          maxWidth: '100%',
          mx: 'auto',
          pb: {
            base: hasMobileBottomNav ? 'var(--bottom-tabbar-spacing)' : '0',
            lg: '0',
          },
        })}
      >
        {feature_isUkLicensed && !hideUKRiskWarning ? (
          <UKRiskWarningBanner />
        ) : null}
        {children}
      </main>

      {hasMobileBottomNav ? (
        <HiddenDesktop>
          <div
            className={css({
              contain: 'style',
              containIntrinsicSize: '56px 56px',
            })}
          >
            <MobileNavWithTradeDynamic
              labels={{
                discover: translations.discoverText,
                portfolio: translations.portfolioText,
                trade: translations.tradeText,
              }}
            />
          </div>
        </HiddenDesktop>
      ) : null}

      {!hideDesktopFooter && (
        <Suspense fallback={null}>
          <FooterLinkListByAppId
            translations={{
              customerSupport: translations.customerSupport,
              frequentlyAskedQuestions: translations.frequentlyAskedQuestions,
            }}
          />
        </Suspense>
      )}
    </div>
  );
};
